import API_BASE from '../config';
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, MapPin, Clock, ArrowRight, CheckCircle, Shield } from "lucide-react";
import axios from "axios";

export default function Contact() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", company: "", sector: "", message: "", subject: "general"
  });
  const [status, setStatus] = useState<"idle"|"sending"|"sent"|"error">("idle");

  const subjects = [
    { value: "general",   label: "General Enquiry" },
    { value: "demo",      label: "Book a Demo" },
    { value: "sales",     label: "Sales & Pricing" },
    { value: "support",   label: "Technical Support" },
    { value: "privacy",   label: "Privacy & Data Request" },
    { value: "press",     label: "Press & Media" },
  ];

  async function handleSubmit() {

    setStatus("sending");
    try {
      await axios.post(`${API_BASE}/api/contact`, form);
      setStatus("sent");
    } catch {
      // Fallback — open email client if API fails
      const subject = encodeURIComponent(`VicShield Contact: ${form.subject}`);
      const body = encodeURIComponent(
        `Name: ${form.name}\nEmail: ${form.email}\nCompany: ${form.company}\nSector: ${form.sector}\n\nMessage:\n${form.message}`
      );
      window.location.href = `mailto:support@vicshield.com?subject=${subject}&body=${body}`;
      setStatus("sent");
    }
  }

  const stagger = (i: number) => ({
    initial: { opacity: 0, y: 24 } as const,
    animate: { opacity: 1, y: 0 } as const,
    transition: { duration: 0.5, delay: i * 0.08 }
  });

  return (
    <div className="bg-white font-['Inter'] min-h-screen">

      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <div onClick={() => navigate("/")}
            className="flex items-center gap-3 cursor-pointer group">
            <div className="w-9 h-9 bg-red-600 rounded-xl flex items-center justify-center
              shadow-lg shadow-red-200 group-hover:scale-105 transition-transform">
              <span className="text-white font-black text-base">V</span>
            </div>
            <span className="text-xl font-black tracking-tighter text-slate-900 uppercase">
              VicShield
            </span>
          </div>
          <button onClick={() => navigate(-1)}
            className="text-xs font-black uppercase tracking-widest text-slate-400
              hover:text-slate-900 transition-colors">
            ← Back
          </button>
        </div>
      </nav>

      {/* Hero */}
      <div className="bg-[#0f172a] py-16">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...stagger(0)}>
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-500 mb-3">
              Contact
            </p>
            <h1 className="text-4xl font-black text-white tracking-tighter uppercase mb-4">
              Get In Touch
            </h1>
            <p className="text-slate-400 font-medium max-w-xl">
              Whether you need a demo, have a question about pricing, or need technical support —
              we respond to every message within 24 hours.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Left — Contact Info */}
          <div className="space-y-6">

            {/* Response time */}
            <motion.div {...stagger(1)}
              className="bg-slate-50 rounded-[2rem] p-6 border border-slate-100">
              <div className="w-10 h-10 bg-red-600 rounded-xl flex items-center justify-center mb-4">
                <Clock size={18} className="text-white" />
              </div>
              <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight mb-2">
                Response Time
              </h3>
              <p className="text-sm font-medium text-slate-500 leading-relaxed">
                We respond to all enquiries within <strong className="text-slate-900">24 hours</strong> Monday to Friday.
                Enterprise support clients receive priority response within 4 hours.
              </p>
            </motion.div>

            {/* Email */}
            <motion.div {...stagger(2)}
              className="bg-slate-50 rounded-[2rem] p-6 border border-slate-100">
              <div className="w-10 h-10 bg-[#0f172a] rounded-xl flex items-center justify-center mb-4">
                <Mail size={18} className="text-white" />
              </div>
              <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight mb-2">
                Email Us Directly
              </h3>
              <a href="mailto:support@vicshield.com"
                className="text-sm font-black text-red-600 hover:text-red-500 transition-colors">
                support@vicshield.com
              </a>
              <p className="text-xs font-medium text-slate-400 mt-2">
                For general enquiries, support, and billing questions
              </p>
            </motion.div>

            {/* Location */}
            <motion.div {...stagger(3)}
              className="bg-slate-50 rounded-[2rem] p-6 border border-slate-100">
              <div className="w-10 h-10 bg-[#0f172a] rounded-xl flex items-center justify-center mb-4">
                <MapPin size={18} className="text-white" />
              </div>
              <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight mb-2">
                Registered Location
              </h3>
              <p className="text-sm font-medium text-slate-500 leading-relaxed">
                Lithuania, European Union
              </p>
              <p className="text-xs font-medium text-slate-400 mt-2">
                GDPR compliant · EU jurisdiction · Global operations
              </p>
            </motion.div>

            {/* Trust signal */}
            <motion.div {...stagger(4)}
              className="bg-[#0f172a] rounded-[2rem] p-6">
              <div className="flex items-center gap-3 mb-4">
                <Shield size={18} className="text-red-400" />
                <h3 className="text-sm font-black text-white uppercase tracking-tight">
                  Data Privacy
                </h3>
              </div>
              <p className="text-xs font-medium text-slate-400 leading-relaxed">
                Any information you submit through this form is processed in accordance with our
                Privacy Policy and GDPR regulations. We never share your details with third parties.
              </p>
              <button onClick={() => navigate("/privacy")}
                className="mt-3 text-xs font-black text-red-400 hover:text-red-300 
                  transition-colors uppercase tracking-widest">
                Read Privacy Policy →
              </button>
            </motion.div>

          </div>

          {/* Right — Contact Form */}
          <motion.div {...stagger(2)} className="lg:col-span-2">
            {status === "sent" ? (
              <div className="bg-slate-50 rounded-[2.5rem] border border-slate-100 p-12
                flex flex-col items-center justify-center text-center h-full">
                <div className="w-20 h-20 bg-green-50 border-2 border-green-200 rounded-2xl
                  flex items-center justify-center mb-6">
                  <CheckCircle size={36} className="text-green-600" />
                </div>
                <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight mb-3">
                  Message Sent
                </h2>
                <p className="text-sm font-medium text-slate-500 max-w-sm leading-relaxed mb-8">
                  Thank you for reaching out. A member of the VicShield team will respond
                  to your message within 24 hours.
                </p>
                <button onClick={() => navigate("/")}
                  className="inline-flex items-center gap-2 bg-[#0f172a] text-white
                    px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-widest
                    hover:bg-slate-800 transition-colors">
                  Back to Home <ArrowRight size={14} />
                </button>
              </div>
            ) : (
              <div className="bg-slate-50 rounded-[2.5rem] border border-slate-100 p-8">
                <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight mb-2">
                  Send a Message
                </h2>
                <p className="text-sm font-medium text-slate-400 mb-8">
                  Fill in the form and we will get back to you within 24 hours.
                </p>

                <div className="space-y-5">

                  {/* Subject */}
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest
                      text-slate-500 mb-2">
                      What can we help you with?
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {subjects.map(s => (
                        <button key={s.value} type="button"
                          onClick={() => setForm(p => ({...p, subject: s.value}))}
                          className={`px-4 py-2 rounded-xl text-xs font-black uppercase 
                            tracking-wider transition-all ${
                            form.subject === s.value
                              ? "bg-[#0f172a] text-white"
                              : "bg-white border border-slate-200 text-slate-500 hover:border-slate-400"
                          }`}>
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Name + Email */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest
                        text-slate-500 mb-2">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input type="text" required
                        value={form.name}
                        onChange={e => setForm(p => ({...p, name: e.target.value}))}
                        placeholder="Your full name"
                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl
                          text-sm font-medium text-slate-900 placeholder-slate-300
                          focus:outline-none focus:border-slate-900 transition-colors" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest
                        text-slate-500 mb-2">
                        Work Email <span className="text-red-500">*</span>
                      </label>
                      <input type="email" required
                        value={form.email}
                        onChange={e => setForm(p => ({...p, email: e.target.value}))}
                        placeholder="you@company.com"
                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl
                          text-sm font-medium text-slate-900 placeholder-slate-300
                          focus:outline-none focus:border-slate-900 transition-colors" />
                    </div>
                  </div>

                  {/* Company + Sector */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest
                        text-slate-500 mb-2">
                        Company Name
                      </label>
                      <input type="text"
                        value={form.company}
                        onChange={e => setForm(p => ({...p, company: e.target.value}))}
                        placeholder="Your company"
                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl
                          text-sm font-medium text-slate-900 placeholder-slate-300
                          focus:outline-none focus:border-slate-900 transition-colors" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest
                        text-slate-500 mb-2">
                        Sector
                      </label>
                      <select
                        value={form.sector}
                        onChange={e => setForm(p => ({...p, sector: e.target.value}))}
                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl
                          text-sm font-medium text-slate-900
                          focus:outline-none focus:border-slate-900 transition-colors">
                        <option value="">Select your sector</option>
                        <option>Fintech & Payments</option>
                        <option>Finance & Banking</option>
                        <option>Insurance</option>
                        <option>Healthcare & Medical</option>
                        <option>Technology & SaaS</option>
                        <option>Retail & E-Commerce</option>
                        <option>Manufacturing</option>
                        <option>Legal & Professional Services</option>
                        <option>Education</option>
                        <option>Government & Public Sector</option>
                        <option>Energy & Utilities</option>
                        <option>Media & Communications</option>
                        <option>Logistics & Supply Chain</option>
                        <option>Other</option>
                      </select>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest
                      text-slate-500 mb-2">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea required rows={5}
                      value={form.message}
                      onChange={e => setForm(p => ({...p, message: e.target.value}))}
                      placeholder="Tell us what you need..."
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl
                        text-sm font-medium text-slate-900 placeholder-slate-300
                        focus:outline-none focus:border-slate-900 transition-colors resize-none" />
                  </div>

                  {/* Submit */}
                  <button type="button" onClick={handleSubmit} disabled={status === "sending"}
                    className="w-full flex items-center justify-center gap-2 bg-red-600
                      text-white py-4 rounded-2xl font-black text-xs uppercase tracking-widest
                      hover:bg-red-500 transition-colors disabled:opacity-50">
                    {status === "sending" ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white
                          rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>Send Message <ArrowRight size={14} /></>
                    )}
                  </button>

                  <p className="text-[10px] font-medium text-slate-400 text-center">
                    By submitting this form you agree to our{" "}
                    <button onClick={() => navigate("/privacy")}
                      className="underline hover:text-slate-600 transition-colors">
                      Privacy Policy
                    </button>
                  </p>

                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-6 px-6 mt-8">
        <div className="max-w-6xl mx-auto flex justify-between items-center flex-wrap gap-4">
          <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
            © 2026 VicShield. All rights reserved.
          </span>
          <div className="flex gap-6">
            <button onClick={() => navigate("/privacy")}
              className="text-[10px] font-black uppercase tracking-widest text-slate-400
                hover:text-red-500 transition-colors">
              Privacy Policy
            </button>
            <button onClick={() => navigate("/terms")}
              className="text-[10px] font-black uppercase tracking-widest text-slate-400
                hover:text-red-500 transition-colors">
              Terms of Service
            </button>
          </div>
        </div>
      </footer>

    </div>
  );
}
